import { ApolloError } from '@apollo/client';

import {
  GetExternalValidationResultQuery as GetExternalValidationResult,
  RegistrationInput,
} from '__generated__/graphql';

import { ParticipantField, ParticipantFieldEntryInput } from '../ParticipantFieldInputProps';
import GetExternalValidationResultQuery from './GetExternalValidationResultQuery';
import useLocale from '../../useLocale';
import useQuery from '../../../api/useQuery';

export interface UseExternalValidationProps {
  field: ParticipantField;
  input: ParticipantFieldEntryInput;
  enabled: boolean;
  registration?: RegistrationInput;
  onSuccess?: (data: GetExternalValidationResult) => unknown;
  onError?: (error: ApolloError) => unknown;
}

const useExternalValidation = ({
  field,
  input,
  enabled,
  registration,
  onSuccess,
  onError,
}: UseExternalValidationProps) => {
  const { locale } = useLocale();

  const { loading } = useQuery(
    GetExternalValidationResultQuery,
    {
      variables: {
        input: {
          participant_field: { id: field.id },
          value: input.value,
          choices: input.choices,
          registration: registration ? {
            id: registration.id,
            ticket: registration.ticket,
            time_slot: registration.time_slot,
            purchase: registration.purchase,
            upgrades: registration.upgrades,
            team: registration.team,
            participant: registration.participant,
            details: registration.details,
            charity: registration.charity,
            fields: registration.fields,
          } : null,
          locale,
        },
      },
      skip: !enabled,
      // Without nextFetchPolicy, the onCompleted() callback is not
      // executed on subsequent fetches with the same variables.
      nextFetchPolicy: 'network-only',
      onCompleted: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    },
  );

  return {
    validating: loading,
  };
};

export default useExternalValidation;
